import DjangoMessages from 'components/DjangoMessages';
import Radio from 'components/Form/Base/BaseRadio';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import {useEffect, useState} from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiErrorAxios} from 'utils/request-axios/utils';

import {BackendData} from '../../types';
import {sendingSettingsLinks} from '../../utils';
import useSendingSettings from '../services';

const DocumentArchive = () => {
    const methods = useFormContext<FormData>();
    const checkboxEnabled = methods.watch(
        'createDocumentArchiveForMarkedAsSent',
    );

    useEffect(() => {
        if (!checkboxEnabled) {
            methods.setValue('defaultMarkedAsSentArchiveSource', null);
        }
    }, [checkboxEnabled]);

    return (
        <Field
            label={t`Default when updating Document Sent Date`}
            tooltip={{
                message: t`You can set a default for Document Archive Creation when you update the Document Sent Date via UI.`,
            }}
            type="value"
        >
            <Checkbox
                label={t`Create Document Archive`}
                name="createDocumentArchiveForMarkedAsSent"
            />
            <Radio
                className="mt-2 border-l border-grey-300 pl-4"
                name="defaultMarkedAsSentArchiveSource"
                options={[
                    {
                        disabled: !checkboxEnabled,
                        label: t`Sent electronically outside MakeLeaps`,
                        value: 'manually_sent_electronically',
                    },
                    {
                        disabled: !checkboxEnabled,
                        label: t`Sent paper copy outside MakeLeaps`,
                        value: 'manually_sent_physically',
                    },
                ]}
            />
        </Field>
    );
};

export type FormData = {
    createDocumentArchiveForMarkedAsSent: boolean;
    defaultMarkedAsSentArchiveSource: null | string;
};

const useInitialValue = (): FormData => {
    const {data} = useBackend<BackendData>();
    const [result] = useState<FormData>(() => {
        return {
            createDocumentArchiveForMarkedAsSent:
                data.createDocumentArchiveForMarkedAsSent,
            defaultMarkedAsSentArchiveSource:
                data.defaultMarkedAsSentArchiveSource,
        };
    });
    return result;
};

const Page = () => {
    const initialData = useInitialValue();
    const methods = useForm<FormData>({
        defaultValues: initialData,
        shouldUnregister: true,
    });
    const api = useSendingSettings();

    const disableSubmit = methods.formState.isSubmitting;

    const handleSubmit = async (formData: FormData) => {
        await api
            .mutateAsync(formData)
            .then(() => {
                setUrl(sendingSettingsLinks.sendingSettingsView);
            })
            .catch(onApiErrorAxios(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <DjangoMessages className="mb-4" />
                <NavigationTabs.Partner selection="sending_settings" />
                <div>
                    <SectionHeader title={t`Document Archive`} />
                    <SideBySide
                        className="mt-4"
                        classNameLabel="w-[14rem] max-w-[14rem]"
                    >
                        <DocumentArchive />
                        <FormResult />
                        <FormActions align="left">
                            <SubmitButton
                                disabled={disableSubmit}
                            >{t`Save`}</SubmitButton>
                            <LinkButton
                                href={sendingSettingsLinks.sendingSettingsView}
                            >{t`Cancel`}</LinkButton>
                        </FormActions>
                    </SideBySide>
                </div>
            </form>
        </FormProvider>
    );
};
export default Page;
